import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { adminApiError, evalsApi } from '../adminApi';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import { modelShortName, shortId } from '../evals/format';
import {
  CALIBRATION_DEFAULT_SIZE,
  CALIBRATION_MAX_SIZE,
  calibrationErrorText,
  drawRequest,
  progressText,
  reportHref,
  setHref,
  setLineages,
  type CalibrationSetSummary,
} from '../evals/labels';
import { ProgressBar } from '../evals/LabelItemView';
import { labelsApi } from '../evals/labelsApi';
import type { EvalRunListItem } from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';
// The Labels pages' styles (TOM-141), class-scoped to .eval-label-*.
import '../../styles/admin-eval-labels.css';

// Judge calibration (TOM-141; ADR 0234 as amended by ADR 0252): every
// calibration set version, newest first, and the form that draws a new set.
// A set is a frozen sample of judged replies from stored collect runs; the
// admin labels each item blind, and the report says how far each judge agrees.
// Nothing on this page reads a verdict.

/** The stored runs a draw may be limited to: finished collect runs. */
function RunPicker({
  runs,
  picked,
  onChange,
}: {
  runs: EvalRunListItem[] | null;
  picked: readonly string[];
  onChange: (ids: string[]) => void;
}) {
  if (runs === null) return <p className="admin-muted eval-sub">Loading stored runs…</p>;
  if (runs.length === 0) {
    return <p className="admin-muted eval-sub">No succeeded collect run is stored yet.</p>;
  }
  const toggle = (id: string) =>
    onChange(picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id]);
  return (
    <div className="eval-label-runs scroll-x" data-testid="eval-label-run-picker">
      <table className="admin-table eval-table eval-table--compact">
        <thead>
          <tr>
            <th aria-label="Pick" />
            <th>Run</th>
            <th>Suite</th>
            <th>Models</th>
            <th>Judge</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id}>
              <td>
                <input
                  type="checkbox"
                  checked={picked.includes(r.id)}
                  onChange={() => toggle(r.id)}
                  aria-label={`Draw from run ${r.id}`}
                  data-testid={`eval-label-run-${r.id}`}
                />
              </td>
              <td className="eval-nowrap">
                <code title={r.id}>{shortId(r.id)}</code>{' '}
                <span className="admin-muted eval-sub">
                  {formatDate(r.startedAt ?? r.createdAt)}
                </span>
              </td>
              <td>{r.suite}</td>
              <td>
                {r.models.map((m) => (
                  <code key={`${m.model}|${m.role}`} title={m.model} className="eval-label-model">
                    {modelShortName(m.model)}
                  </code>
                ))}
              </td>
              <td className="eval-nowrap">
                {r.judges.length > 0 ? (
                  r.judges.map(modelShortName).join(', ')
                ) : (
                  <span className="admin-muted">none</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DrawSet() {
  const navigate = useNavigate();
  const [size, setSize] = useState(String(CALIBRATION_DEFAULT_SIZE));
  const [seed, setSeed] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [limit, setLimit] = useState(false);
  const [runIds, setRunIds] = useState<string[]>([]);
  const [runs, setRuns] = useState<EvalRunListItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!limit || runs !== null) return;
    let ignore = false;
    evalsApi
      .runs({ runKind: 'collect', status: 'succeeded', limit: 200 })
      .then((res) => {
        if (!ignore) setRuns(res.runs);
      })
      .catch(() => {
        if (!ignore) setRuns([]);
      });
    return () => {
      ignore = true;
    };
  }, [limit, runs]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const checked = drawRequest({ size, seed, includeTest, runIds: limit ? runIds : [] });
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    if (limit && runIds.length === 0) {
      setError('Pick at least one run, or draw from every stored run.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await labelsApi.createSet(checked.body);
      navigate(setHref(created.setId));
    } catch (err) {
      const body = adminApiError(err);
      setError(calibrationErrorText(body.error, body.message));
      setBusy(false);
    }
  }

  return (
    <form className="eval-card eval-label-draw" onSubmit={submit} data-testid="eval-label-draw">
      <h2 className="eval-label-h2">Draw a new set</h2>
      <p className="admin-hint">
        Samples judged replies from finished collect runs, stratified by suite, case kind and the
        run&apos;s own judge verdict, and freezes each case as the judge saw it. A reply any set
        already holds is never drawn again.
      </p>
      <div className="eval-filters">
        <label>
          Sample size
          <input
            type="text"
            inputMode="numeric"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            aria-describedby="eval-label-size-hint"
            data-testid="eval-label-size"
          />
        </label>
        <label>
          Seed (optional)
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="minted when blank"
            maxLength={200}
            data-testid="eval-label-seed"
          />
        </label>
      </div>
      <p id="eval-label-size-hint" className="admin-muted eval-sub">
        1 to {CALIBRATION_MAX_SIZE}. A stratum with too few replies is reported, never padded.
      </p>
      <label className="eval-label-check">
        <input
          type="checkbox"
          checked={includeTest}
          onChange={(e) => setIncludeTest(e.target.checked)}
          data-testid="eval-label-include-test"
        />
        <span>
          Include the held-out test split
          <span className="admin-muted eval-sub eval-label-check-hint">
            Long-suite test replies are drawn too. Each stays hidden until you reveal it while
            labelling, and every reveal is audited (ADR 0238).
          </span>
        </span>
      </label>
      <label className="eval-label-check">
        <input
          type="checkbox"
          checked={limit}
          onChange={(e) => setLimit(e.target.checked)}
          data-testid="eval-label-limit-runs"
        />
        <span>
          Draw only from runs I pick
          <span className="admin-muted eval-sub eval-label-check-hint">
            The set records the runs, so the draw can be repeated (ADR 0252).
          </span>
        </span>
      </label>
      {limit ? <RunPicker runs={runs} picked={runIds} onChange={setRunIds} /> : null}
      <div className="eval-label-save">
        <button
          type="submit"
          className="admin-button-affirm"
          disabled={busy}
          data-testid="eval-label-draw-submit"
        >
          {busy ? 'Drawing…' : 'Draw set'}
        </button>
      </div>
      {error !== null ? (
        <p className="admin-auth-banner error" role="alert" data-testid="eval-label-draw-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function SetRow({ set }: { set: CalibrationSetSummary }) {
  const others = set.labellers.filter((l) => l.labelled > 0).length;
  return (
    <tr data-testid={`eval-label-set-${set.id}`}>
      <td className="eval-nowrap eval-label-cell-main">
        <Link to={setHref(set.id)}>Version {set.version}</Link>{' '}
        {set.superseded ? (
          <EvalBadge tone="muted">Superseded</EvalBadge>
        ) : (
          <EvalBadge tone="succeeded">Latest</EvalBadge>
        )}
      </td>
      <td className="eval-nowrap" data-label="Drawn">
        <span className="eval-stack">
          <span>{formatDate(set.createdAt)}</span>
          <span className="admin-muted eval-sub">{set.createdBy ?? 'unknown admin'}</span>
        </span>
      </td>
      <td className="eval-nowrap" data-label="Items">
        {set.items}
        {set.testItems > 0 ? (
          <span className="admin-muted eval-sub"> · {set.testItems} test</span>
        ) : null}
      </td>
      <td data-label="You">
        <ProgressBar labelled={set.labelledByYou} total={set.items} />
      </td>
      <td className="eval-nowrap" data-label="Labellers">
        {others === 0 ? <span className="admin-muted">nobody yet</span> : `${others}`}
      </td>
      <td className="eval-nowrap">
        <span className="eval-label-row-actions">
          <Link to={setHref(set.id)}>{set.labelledByYou < set.items ? 'Label' : 'Items'}</Link>
          <Link to={reportHref(set.id)}>Report</Link>
        </span>
      </td>
    </tr>
  );
}

export function AdminEvalLabels() {
  const [sets, setSets] = useState<CalibrationSetSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    labelsApi
      .sets()
      .then((s) => {
        if (!ignore) setSets(s);
      })
      .catch((err) => {
        const body = adminApiError(err);
        if (!ignore)
          setError(calibrationErrorText(body.error, body.message ?? 'Failed to load sets.'));
      });
    return () => {
      ignore = true;
    };
  }, []);

  const lineages = sets === null ? [] : setLineages(sets);
  const resume = sets?.find((s) => !s.superseded && s.labelledByYou < s.items) ?? null;

  return (
    <section className="admin-section eval-page">
      <header className="admin-section-header">
        <h1>Judge calibration</h1>
        <span className="admin-count">
          {sets !== null ? `${lineages.length} ${lineages.length === 1 ? 'set' : 'sets'}` : null}
        </span>
        <GuideLink section="labels" />
      </header>
      <p className="admin-hint">
        Label a frozen sample of judged replies blind, then read how far each judge agrees with you.
        A judge changes only on measured agreement, and only from a fully labelled set (ADR 0234,
        ADR 0252). The judge&apos;s verdict on an item shows only after you save your label. Do not
        look an item up on the run pages before labelling it.
      </p>

      <EvalError message={error} />
      {sets === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {resume !== null ? (
        <p className="eval-actions">
          <Link
            className="admin-button-secondary"
            to={setHref(resume.id)}
            data-testid="eval-label-resume"
          >
            Continue labelling set {shortId(resume.lineageId)} v{resume.version} →
          </Link>
          <span className="admin-muted eval-sub">
            {progressText(resume.labelledByYou, resume.items)} by you
          </span>
        </p>
      ) : null}

      {sets !== null && sets.length === 0 ? (
        <div className="admin-empty-state">
          <p className="admin-empty-state-title">No calibration set yet.</p>
          <p className="admin-hint">Draw one below from the stored collect runs.</p>
        </div>
      ) : null}

      {lineages.length > 0 ? (
        <div className="scroll-x">
          <table
            className="admin-table eval-table eval-label-stack-table"
            data-testid="eval-label-sets"
          >
            <thead>
              <tr>
                <th>Version</th>
                <th>Drawn</th>
                <th>Items</th>
                <th>Your labels</th>
                <th>Labellers</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            {lineages.map((l) => (
              <tbody key={l.lineageId} className="eval-run-group">
                <tr className="eval-label-lineage">
                  <td colSpan={6}>
                    Set <code title={l.lineageId}>{shortId(l.lineageId)}</code>
                    <span className="admin-muted eval-sub">
                      {' '}
                      · {l.versions.length} {l.versions.length === 1 ? 'version' : 'versions'}
                    </span>
                  </td>
                </tr>
                {l.versions.map((s) => (
                  <SetRow key={s.id} set={s} />
                ))}
              </tbody>
            ))}
          </table>
        </div>
      ) : null}

      <DrawSet />
    </section>
  );
}
